import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

/**
 * Searchable typeahead select.
 *
 * Props:
 *   value            - currently selected option's id (string)
 *   onChange(id)     - called with the new id when an option is picked, or '' when cleared
 *   options          - array of items
 *   getId(opt)       - returns the stable id (defaults to opt._id)
 *   getLabel(opt)    - primary label (defaults to opt.name or first+last)
 *   getSearchText    - returns a single concatenated string used for filtering
 *   renderOption     - custom JSX for a row in the dropdown (gets the option)
 *   renderSelected   - custom JSX for the selected pill in the input area
 *   placeholder      - placeholder text
 *   required         - HTML required indicator (form validation)
 *   testId           - data-testid prefix
 *   disabled
 */
export default function SearchableSelect({
  value, onChange, options = [],
  getId = (o) => o._id,
  getLabel = (o) => o.name || `${o.first_name || ''} ${o.last_name || ''}`.trim(),
  getSearchText,
  renderOption, renderSelected,
  placeholder = 'Search…',
  required = false, disabled = false, testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find(o => getId(o) === value) || null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, value]
  );

  // Close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const norm = (s) => (s == null ? '' : String(s)).toLowerCase().trim();
  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return options;
    return options.filter(o => {
      const text = getSearchText
        ? getSearchText(o)
        : `${getLabel(o)} ${o.phone || ''} ${o.email || ''}`;
      return norm(text).includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, options]);

  const select = (o) => {
    onChange(getId(o));
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      {/* Hidden input keeps HTML5 required validation working */}
      <input type="text" value={value || ''} required={required} readOnly
             tabIndex={-1} aria-hidden="true"
             className="sr-only" onChange={() => {}} />
      {selected && !open ? (
        // Selected pill view
        <div
          onClick={() => { if (!disabled) { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); } }}
          className={`w-full min-h-9 rounded-xl border border-[#E5F2F1] px-3 py-1.5 text-sm flex items-center justify-between gap-2 bg-white ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-[#0D8B7D]'}`}
          data-testid={testId ? `${testId}-selected` : undefined}
        >
          <div className="flex-1 min-w-0">
            {renderSelected ? renderSelected(selected) : <span className="font-medium text-[#1A3330] truncate block">{getLabel(selected)}</span>}
          </div>
          {!disabled && (
            <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }}
                    className="text-[#4A6B68] hover:text-[#D3544E] flex-shrink-0"
                    data-testid={testId ? `${testId}-clear` : undefined}
                    aria-label="Clear">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        // Search input view
        <div className="relative">
          <Search className="w-4 h-4 text-[#4A6B68] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full h-9 rounded-xl border border-[#E5F2F1] pl-9 pr-9 text-sm focus:outline-none focus:border-[#0D8B7D]"
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <ChevronDown
            className={`w-4 h-4 text-[#4A6B68] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      )}

      {/* Dropdown panel */}
      {open && !disabled && (
        <div className="absolute z-50 left-0 right-0 top-[calc(100%+4px)] bg-white border border-[#E5F2F1] rounded-xl shadow-lg max-h-72 overflow-auto"
             data-testid={testId ? `${testId}-dropdown` : undefined}>
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-sm text-[#4A6B68] text-center" data-testid={testId ? `${testId}-empty` : undefined}>
              No results found
            </div>
          ) : filtered.map(o => {
            const id = getId(o);
            const isActive = id === value;
            return (
              <button key={id} type="button" onClick={() => select(o)}
                      className={`w-full text-left px-3 py-2 hover:bg-[#F4FAF9] border-b border-[#F4FAF9] last:border-b-0 ${isActive ? 'bg-[#E8F5F3]' : ''}`}
                      data-testid={testId ? `${testId}-option-${id}` : undefined}>
                {renderOption ? renderOption(o) : <span className="text-sm text-[#1A3330]">{getLabel(o)}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
